import { useCallback, useEffect, useState } from "react";
import { HauffeLoader } from "../components/BrandLogo";
import { Layout } from "../components/Layout";
import { Panel, Stat, StatusBadge } from "../components/Panel";
import {
  fetchRuntimeWorkspace,
  generateRuntimeAiSummary,
  openRuntimeValueRound,
  type RuntimeWorkspaceOverview
} from "../lib/api/runtimeWorkspaceClient";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { resolveAiSummaryForLanguage } from "../lib/i18n/languageResolvers";
import { downloadAiSummaryPdf } from "../lib/pdf/aiSummaryPdf";
import { customerValueProcessLabel, type WorkspaceProcessStatus } from "../lib/valueWorkflow";

function formatCustomerError(caught: unknown): string | undefined {
  if (!(caught instanceof Error) || !caught.message) return undefined;
  return caught.message;
}

function toWorkspaceRecommendation(item: string, language: "de" | "en"): string {
  const normalized = item.toLowerCase();
  if (normalized.includes("scoring") || normalized.includes("pilot") || normalized.includes("pilotiere")) {
    return language === "en"
      ? "Start with an initial evaluation of selected customers in the digital Customer Value Model and check whether the segmentation reflects actual customer quality in a traceable way."
      : "Starten Sie mit einer ersten Bewertung ausgewählter Kunden im digitalen Customer Value Modell und prüfen Sie, ob die Segmentierung die tatsächliche Kundenqualität nachvollziehbar abbildet.";
  }
  if (normalized.includes("workshop") || normalized.includes("moderiert") || normalized.includes("termin") || normalized.includes("addon")) {
    if (normalized.includes("friction") || normalized.includes("reibung")) {
      return language === "en"
        ? "Use the visible friction points as checkpoints for the Customer Value Calculation, especially around target customers, relationship quality and operational effort."
        : "Nutzen Sie die sichtbaren Reibungspunkte als Prüfpunkte für die Customer Value Calculation, insbesondere bei Zielkunden, Beziehungsqualität und operativem Aufwand.";
    }
    return language === "en"
      ? "Review the suggested Customer Value Criteria in the workspace and finalize the weighting and evaluation logic together with the Company Owner."
      : "Prüfen Sie die vorgeschlagenen Customer-Value-Kriterien im Workspace und finalisieren Sie gemeinsam mit dem Company Owner die Gewichtungen und Bewertungslogik.";
  }
  if (normalized.includes("crm")) {
    return language === "en"
      ? "Adopt the suggested CRM fields as the data foundation for later evaluation and segmentation."
      : "Übernehmen Sie die vorgeschlagenen CRM-Felder als Datenbasis für spätere Auswertung und Segmentierung.";
  }
  return item;
}

function getNextStepLabel(status: WorkspaceProcessStatus | undefined, hasCalculation: boolean | undefined, language: "de" | "en"): string {
  const copy = language === "en" ? {
    openValueRound: "Open Value Round",
    valueRoundRunning: "Value Round running",
    clusterCriteria: "Cluster Customer Value Criteria",
    reviewCriteria: "Review criteria",
    distributeImportance: "Distribute importance",
    calculationConfirmed: "View Customer Value Calculation",
    reviewCalculation: "Review Customer Value Calculation"
  } : {
    openValueRound: "Value-Runde öffnen",
    valueRoundRunning: "Value-Runde läuft",
    clusterCriteria: "Customer-Value-Kriterien clustern",
    reviewCriteria: "Kriterien prüfen",
    distributeImportance: "Wichtigkeit verteilen",
    calculationConfirmed: "Customer Value Calculation ansehen",
    reviewCalculation: "Customer Value Calculation prüfen"
  };
  if (!status || status === "department_input_open" || status === "hauffe_os_evaluation_ready") return copy.openValueRound;
  if (status === "value_round_open") return copy.valueRoundRunning;
  if (status === "value_round_completed") return copy.clusterCriteria;
  if (status === "clusters_ready_for_owner_review") return copy.reviewCriteria;
  if (status === "weighting_open") return copy.distributeImportance;
  if (status === "calculation_confirmed") return copy.calculationConfirmed;
  if (status === "calculation_ready_for_owner_review" || hasCalculation) return copy.reviewCalculation;
  return copy.openValueRound;
}

function processStepStatus(step: "summary" | "value" | "clusters" | "weighting" | "calculation" | "confirmed", status: WorkspaceProcessStatus | undefined, hasSummary: boolean | undefined, hasCalculation: boolean | undefined, language: "de" | "en"): string {
  const copy = language === "en" ? {
    completed: "Completed",
    open: "Open",
    closed: "Closed",
    ready: "Ready",
    readyForReview: "Ready for review",
    confirmed: "Confirmed"
  } : {
    completed: "Abgeschlossen",
    open: "Offen",
    closed: "Geschlossen",
    ready: "Bereit",
    readyForReview: "Bereit zur Prüfung",
    confirmed: "Bestätigt"
  };
  if (step === "summary") return hasSummary ? copy.completed : copy.open;
  if (!hasSummary) return copy.closed;
  if (step === "value") {
    if (status === "value_round_open") return copy.open;
    if (status && ["value_round_completed", "clusters_ready_for_owner_review", "weighting_open", "calculation_ready_for_owner_review", "calculation_confirmed"].includes(status)) return copy.completed;
    return copy.ready;
  }
  if (step === "clusters") {
    if (status === "value_round_completed") return copy.open;
    if (status === "clusters_ready_for_owner_review") return copy.readyForReview;
    if (status && ["weighting_open", "calculation_ready_for_owner_review", "calculation_confirmed"].includes(status)) return copy.completed;
    return copy.closed;
  }
  if (step === "weighting") {
    if (status === "weighting_open") return copy.open;
    if (status && ["calculation_ready_for_owner_review", "calculation_confirmed"].includes(status)) return copy.completed;
    return copy.closed;
  }
  if (step === "calculation") {
    if (status === "calculation_confirmed") return copy.completed;
    if (status === "calculation_ready_for_owner_review" || hasCalculation) return copy.readyForReview;
    return copy.closed;
  }
  return status === "calculation_confirmed" ? copy.confirmed : copy.closed;
}

function processStepTone(stepStatus: string, language: "de" | "en"): "default" | "success" | "warning" {
  const successLabels = language === "en" ? ["Completed", "Confirmed"] : ["Abgeschlossen", "Bestätigt"];
  const warningLabels = language === "en" ? ["Open", "Ready", "Ready for review"] : ["Offen", "Bereit", "Bereit zur Prüfung"];
  if (successLabels.includes(stepStatus)) return "success";
  if (warningLabels.includes(stepStatus)) return "warning";
  return "default";
}

export function AiSummary() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "HAUFFE OS Facilitator",
    title: "HAUFFE OS Evaluation",
    description: "The HAUFFE OS Facilitator condenses submitted department perspectives and makes Customer Value Criteria, decision friction and recommended next steps visible.",
    loadError: "HAUFFE OS Evaluation could not be loaded.",
    pdfError: "The PDF evaluation could not be created. Please try again.",
    createdNotice: "HAUFFE OS Evaluation was created.",
    genericError: "Please try again later or contact HAUFFE.",
    valueRoundOpened: "Value Round was opened.",
    valueRoundError: "Value Round could not be opened.",
    loading: "Evaluation is loading",
    loadingBody: "Workspace data is loading...",
    completeErrorTitle: "The HAUFFE OS Evaluation could not be completed.",
    noWorkspaceTitle: "No workspace available yet.",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    submittedDepartments: "Submitted departments",
    evaluation: "HAUFFE OS Evaluation",
    available: "Available",
    notCreated: "Not created yet",
    processStatus: "Process status",
    nextStep: "Next step",
    resultAccess: "Customer Value Calculation",
    requestEvaluation: "Request HAUFFE OS Evaluation",
    noteTitle: "Evaluation note",
    noteBody: "The HAUFFE OS Evaluation is aligned with the booked workspace scope. It condenses submitted department perspectives and opens the next digital step for identifying shared Customer Value Criteria.",
    processOverview: "Process overview",
    processSteps: ["HAUFFE OS Evaluation", "Value Round", "Criteria Clustering", "Weighting", "Customer Value Calculation", "Confirmation"],
    generatingTitle: "Your HAUFFE OS Evaluation is being created",
    generatingLabel: "HAUFFE OS is working on your evaluation",
    generatingBody: "The HAUFFE OS Facilitator analyzes the submitted department perspectives, condenses relevant patterns and prepares the next steps for the Customer Value Calculation. This can take up to 3 minutes.",
    generatingNote: "Please do not close this window and do not start the process again.",
    processingData: "HAUFFE OS is processing the data ...",
    processingStay: "This may take a moment. Please stay on this page.",
    noAnswersTitle: "No answers yet",
    noAnswersBody: "No answers submitted yet.",
    existingTitle: "HAUFFE OS Evaluation available",
    existingBody: "This evaluation is based on the HAUFFE OS methodology and the submitted department perspectives. It is a prepared working state and must be finally reviewed by the Company Owner.",
    method: "Methodology",
    methodValue: "HAUFFE OS Customer Value Methodology",
    createdAt: "Created at",
    noSummaryTitle: "No HAUFFE OS Evaluation available yet.",
    noSummaryBody: "There is no HAUFFE OS Evaluation for this workspace yet.",
    preparing: "HAUFFE OS Evaluation is being prepared ...",
    inactiveNote: "The workspace must be active before a HAUFFE OS Evaluation can be created.",
    summary: "Summary of department perspectives",
    criteria: "Shared Customer Value Criteria",
    friction: "Decision friction and risks",
    missing: "Missing criteria",
    observationTitle: "Observation, derivation and recommendation",
    observations: "Observations",
    derivations: "Derivations",
    recommendations: "Recommendations",
    nextCriteriaTitle: "Next step: Identify Customer Value Criteria",
    nextCriteriaBody: "The HAUFFE OS Evaluation has made shared patterns, friction points and open clarification questions visible. In the next step, departments additionally capture what matters to them in a valuable customer. From this, Customer Value Criteria are clustered and then weighted.",
    opening: "Value Round is opening ...",
    openValueRound: "Open Value Round",
    pdf: "Download evaluation as PDF",
    saved: "Saved",
    preparingPdf: "PDF is being created ...",
    evaluationLanguageTitle: "Evaluation language",
    evaluationLanguageBody: "This evaluation was created in another language. You can regenerate the evaluation in the current language. Existing results will not be overwritten automatically.",
    regenerateEvaluation: "Regenerate evaluation in current language",
    localGeneratingHelp: "This usually takes up to 3 minutes. Please keep this window open.",
    openRegenerated: "Open regenerated evaluation"
  } : {
    eyebrow: "HAUFFE OS Facilitator",
    title: "HAUFFE OS Auswertung",
    description: "Der HAUFFE OS Facilitator verdichtet eingereichte Abteilungsperspektiven und macht Customer-Value-Kriterien, Entscheidungsreibung und empfohlene nächste Schritte sichtbar.",
    loadError: "HAUFFE OS Auswertung konnte nicht geladen werden.",
    pdfError: "Die PDF-Auswertung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
    createdNotice: "HAUFFE OS Auswertung wurde erstellt.",
    genericError: "Bitte versuchen Sie es später erneut oder wenden Sie sich an HAUFFE.",
    valueRoundOpened: "Value-Runde wurde geöffnet.",
    valueRoundError: "Value-Runde konnte nicht geöffnet werden.",
    loading: "Auswertung wird geladen",
    loadingBody: "Workspace-Daten werden geladen...",
    completeErrorTitle: "Die HAUFFE OS Auswertung konnte nicht abgeschlossen werden.",
    noWorkspaceTitle: "Noch kein Workspace vorhanden.",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace hinterlegt.",
    submittedDepartments: "Eingereichte Abteilungen",
    evaluation: "HAUFFE OS Auswertung",
    available: "Vorhanden",
    notCreated: "Noch nicht erstellt",
    processStatus: "Prozessstatus",
    nextStep: "Nächster Schritt",
    resultAccess: "Customer Value Calculation",
    requestEvaluation: "HAUFFE OS Auswertung abrufen",
    noteTitle: "Auswertungshinweis",
    noteBody: "Die HAUFFE OS Auswertung ist auf den gebuchten Workspace-Umfang abgestimmt. Sie verdichtet eingereichte Abteilungsperspektiven und öffnet den nächsten digitalen Schritt zur Ermittlung gemeinsamer Customer-Value-Kriterien.",
    processOverview: "Prozessübersicht",
    processSteps: ["HAUFFE OS Auswertung", "Value-Runde", "Kriterien-Clustering", "Gewichtung", "Customer Value Calculation", "Bestätigung"],
    generatingTitle: "Ihre HAUFFE OS Auswertung wird erstellt",
    generatingLabel: "HAUFFE OS arbeitet an Ihrer Auswertung",
    generatingBody: "Der HAUFFE OS Facilitator analysiert die eingereichten Abteilungsperspektiven, verdichtet relevante Muster und bereitet die nächsten Schritte für die Customer Value Calculation vor. Dieser Vorgang kann bis zu 3 Minuten dauern.",
    generatingNote: "Bitte schließen Sie dieses Fenster nicht und starten Sie den Vorgang nicht erneut.",
    processingData: "HAUFFE OS verarbeitet die Daten ...",
    processingStay: "Das kann einen Moment dauern. Bitte bleiben Sie auf dieser Seite.",
    noAnswersTitle: "Noch keine Antworten",
    noAnswersBody: "Noch keine Antworten eingereicht.",
    existingTitle: "HAUFFE OS Auswertung vorhanden",
    existingBody: "Diese Auswertung basiert auf der HAUFFE OS Methodik und den eingereichten Abteilungsperspektiven. Sie ist ein vorbereiteter Arbeitsstand und muss final durch den Company Owner geprüft werden.",
    method: "Methodik",
    methodValue: "HAUFFE OS Customer Value Methodik",
    createdAt: "Erstellt am",
    noSummaryTitle: "Noch keine HAUFFE OS Auswertung vorhanden.",
    noSummaryBody: "Es liegt noch keine HAUFFE OS Auswertung für diesen Workspace vor.",
    preparing: "HAUFFE OS Auswertung wird vorbereitet ...",
    inactiveNote: "Der Workspace muss aktiv sein, bevor eine HAUFFE OS Auswertung erstellt werden kann.",
    summary: "Zusammenfassung der Abteilungsperspektiven",
    criteria: "Gemeinsame Customer-Value-Kriterien",
    friction: "Entscheidungsreibung und Risiken",
    missing: "Fehlende Kriterien",
    observationTitle: "Beobachtung, Ableitung und Empfehlung",
    observations: "Beobachtungen",
    derivations: "Ableitungen",
    recommendations: "Empfehlungen",
    nextCriteriaTitle: "Nächster Schritt: Customer-Value-Kriterien ermitteln",
    nextCriteriaBody: "Die HAUFFE OS Auswertung hat gemeinsame Muster, Reibungspunkte und offene Klärungsfragen sichtbar gemacht. Im nächsten Schritt erfassen die Abteilungen zusätzlich, was ihnen an einem wertvollen Kunden wichtig ist. Daraus werden Customer-Value-Kriterien geclustert und anschließend gewichtet.",
    opening: "Value-Runde wird geöffnet ...",
    openValueRound: "Value-Runde öffnen",
    pdf: "Auswertung als PDF herunterladen",
    saved: "Gespeichert",
    preparingPdf: "PDF wird erstellt ...",
    evaluationLanguageTitle: "Auswertungssprache",
    evaluationLanguageBody: "Diese Auswertung wurde in einer anderen Sprache erstellt. Sie können die Auswertung in der aktuellen Sprache neu erstellen. Bestehende Ergebnisse werden nicht automatisch überschrieben.",
    regenerateEvaluation: "Auswertung in aktueller Sprache neu erstellen",
    localGeneratingHelp: "Das dauert normalerweise bis zu 3 Minuten. Bitte lassen Sie dieses Fenster geöffnet.",
    openRegenerated: "Neu erstellte Auswertung öffnen"
  };
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpeningValueRound, setIsOpeningValueRound] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [pdfError, setPdfError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [regeneratedEvaluation, setRegeneratedEvaluation] = useState(false);

  const loadWorkspace = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const nextOverview = await fetchRuntimeWorkspace();
      setOverview(nextOverview);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [copy.loadError]);

  async function handleDownloadPdf() {
    setPdfError(undefined);
    const summary = overview.latestSummary;
    const pdfResult = resolveAiSummaryForLanguage(summary, language);
    if (!overview.workspace || !summary || !pdfResult) return;
    setIsDownloadingPdf(true);
    try {
      await downloadAiSummaryPdf({
        workspaceName: overview.workspace.name,
        createdAt: summary.createdAt,
        result: pdfResult,
        language
      });
    } catch {
      setPdfError(copy.pdfError);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  async function handleGenerateSummary(options?: { regenerate?: boolean }) {
    if (isGenerating || (!canGenerate && !options?.regenerate)) return;
    setIsGenerating(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await generateRuntimeAiSummary(language, options);
      if (options?.regenerate) setRegeneratedEvaluation(true);
      setNotice(copy.createdNotice);
      await loadWorkspace();
    } catch (caught) {
      setError(formatCustomerError(caught) ?? copy.genericError);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleOpenValueRound() {
    setIsOpeningValueRound(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await openRuntimeValueRound();
      setNotice(copy.valueRoundOpened);
      await loadWorkspace();
    } catch (caught) {
      setError(formatCustomerError(caught) ?? copy.valueRoundError);
    } finally {
      setIsOpeningValueRound(false);
    }
  }

  const submittedResponses = overview.departments.filter((department) => department.responseStatus === "submitted").length;
  const canGenerate = Boolean(overview.workspace) && overview.workspace?.status === "active" && submittedResponses > 0 && !overview.latestSummary;
  const displayResult = resolveAiSummaryForLanguage(overview.latestSummary, language);
  const evaluationLanguageMismatch = Boolean(overview.latestSummary && !displayResult);
  const criteriaSuggestions = displayResult?.customer_value_criteria_suggestions ?? [];
  const frictionSignals = displayResult?.decision_friction_signals ?? [];
  const missingCriteria = displayResult?.missing_criteria ?? [];
  const observationSummary = displayResult?.observations_vs_derivations_vs_recommendations_summary;
  const observations = observationSummary?.observations ?? [];
  const derivations = observationSummary?.derivations ?? [];
  const recommendations = observationSummary?.recommendations ?? [];
  const processStatus = overview.process?.status;
  const nextStepLabel = getNextStepLabel(processStatus, Boolean(overview.calculation), language);
  const processCompleted = processStatus === "calculation_confirmed";
  const processReady = processStatus === "calculation_ready_for_owner_review" || Boolean(overview.calculation);
  const submittedTone = submittedResponses === 0
    ? "danger"
    : submittedResponses === overview.departments.length
      ? "success"
      : "warning";
  const nextStepTone = processCompleted || processReady
    ? "success"
    : overview.latestSummary
      ? "warning"
      : "danger";

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      {isLoading ? <Panel title={copy.loading}><p>{copy.loadingBody}</p></Panel> : null}
      {error ? (
        <Panel title={copy.completeErrorTitle} variant="accent">
          <p role="alert">{copy.genericError}</p>
        </Panel>
      ) : null}
      {notice ? (
        <Panel title={copy.saved} variant="accent">
          <p>{notice}</p>
        </Panel>
      ) : null}
      {pdfError ? (
        <Panel title={copy.pdfError} variant="accent">
          <p className="form-error" role="alert">{pdfError}</p>
        </Panel>
      ) : null}
      {!isLoading && !error && !overview.workspace ? (
        <Panel title={copy.noWorkspaceTitle} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}
      {overview.workspace ? (
        <>
          <div className="grid grid-4">
            <Stat label={copy.submittedDepartments} value={`${submittedResponses} / ${overview.departments.length}`} tone={submittedTone} />
            <Stat label={copy.evaluation} value={overview.latestSummary ? copy.available : copy.notCreated} tone={overview.latestSummary ? "success" : "danger"} />
            <Stat label={copy.processStatus} value={customerValueProcessLabel(processStatus, language)} tone={processCompleted ? "success" : processReady ? "warning" : "default"} />
            <Stat label={processCompleted || processReady ? copy.resultAccess : copy.nextStep} value={overview.latestSummary ? nextStepLabel : copy.requestEvaluation} tone={nextStepTone} />
          </div>

          <Panel title={copy.noteTitle}>
            <p>{copy.noteBody}</p>
          </Panel>

          <Panel title={copy.processOverview} variant="accent">
            <div className="process-list">
              {[
                [copy.processSteps[0], processStepStatus("summary", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)],
                [copy.processSteps[1], processStepStatus("value", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)],
                [copy.processSteps[2], processStepStatus("clusters", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)],
                [copy.processSteps[3], processStepStatus("weighting", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)],
                [copy.processSteps[4], processStepStatus("calculation", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)],
                [copy.processSteps[5], processStepStatus("confirmed", processStatus, Boolean(overview.latestSummary), Boolean(overview.calculation), language)]
              ].map(([step, status]) => (
                <div className="process-step" key={step}>
                  <StatusBadge tone={processStepTone(status, language)} withCheck={processStepTone(status, language) === "success"}>
                    {status}
                  </StatusBadge>
                  <strong>{step}</strong>
                </div>
              ))}
            </div>
          </Panel>

          {isGenerating ? (
            <Panel title={copy.generatingTitle} variant="accent">
              <div className="processing-state motion-fade">
                <HauffeLoader label={copy.generatingLabel} />
                <div>
                  <p>{copy.generatingBody}</p>
                  <p className="form-note">{copy.processingData} {copy.processingStay}</p>
                  <p className="form-note">{copy.generatingNote}</p>
                </div>
              </div>
            </Panel>
          ) : null}

          {submittedResponses === 0 ? (
            <Panel title={copy.noAnswersTitle} variant="accent">
              <p>{copy.noAnswersBody}</p>
            </Panel>
          ) : null}

          {overview.latestSummary ? (
            <Panel title={copy.existingTitle} variant="accent">
              <div className="data-list">
                <div>
                  <span>{copy.method}</span>
                  <strong>{copy.methodValue}</strong>
                </div>
                <div>
                  <span>{copy.createdAt}</span>
                  <strong>{new Date(overview.latestSummary.createdAt).toLocaleString(language === "en" ? "en-US" : "de-DE")}</strong>
                </div>
              </div>
              <p className="form-note">{copy.existingBody}</p>
            </Panel>
          ) : (
            <Panel title={copy.noSummaryTitle} variant="accent">
              <p>{copy.noSummaryBody}</p>
              {canGenerate ? (
                <button className="button button-primary" type="button" disabled={isGenerating} onClick={() => void handleGenerateSummary()}>
                  {isGenerating ? copy.preparing : copy.requestEvaluation}
                </button>
              ) : null}
              {!canGenerate && submittedResponses > 0 && overview.workspace?.status !== "active" ? (
                <p className="form-note">{copy.inactiveNote}</p>
              ) : null}
            </Panel>
          )}

          {evaluationLanguageMismatch ? (
            <Panel title={copy.evaluationLanguageTitle} variant="accent">
              <p>{copy.evaluationLanguageBody}</p>
              {isGenerating ? (
                <div className="processing-state motion-fade">
                  <HauffeLoader label={copy.preparing} />
                  <div>
                    <div className="weight-bar" aria-hidden="true"><span style={{ width: "72%" }} /></div>
                    <p className="form-note">{copy.localGeneratingHelp}</p>
                  </div>
                </div>
              ) : null}
              <button className="button button-primary" type="button" disabled={isGenerating} onClick={() => void handleGenerateSummary({ regenerate: true })}>
                {isGenerating ? copy.preparing : copy.regenerateEvaluation}
              </button>
            </Panel>
          ) : null}
          {regeneratedEvaluation && !evaluationLanguageMismatch && overview.latestSummary?.result ? (
            <Panel title={copy.evaluationLanguageTitle} variant="accent">
              <p>{copy.createdNotice}</p>
              <div className="hero-actions">
                <a className="button button-primary" href="#evaluation-result">{copy.openRegenerated}</a>
                <button className="button button-secondary" type="button" onClick={() => void handleDownloadPdf()}>{copy.pdf}</button>
              </div>
            </Panel>
          ) : null}

          {displayResult ? (
            <div id="evaluation-result">
              <Panel title={copy.summary}>
                <p>{displayResult.workspace_summary}</p>
              </Panel>
              <Panel title={copy.criteria}>
                <div className="data-list">
                  {criteriaSuggestions.map((criterion) => (
                    <div key={criterion.name}>
                      <span>{criterion.type}</span>
                      <strong>{criterion.name}</strong>
                      <p>{criterion.description}</p>
                      <p className="muted">{criterion.reason}</p>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title={copy.friction}>
                <div className="data-list">
                  {frictionSignals.map((signal) => (
                    <div key={signal.signal}>
                      <span>{(signal.departments_involved ?? []).join(" · ")}</span>
                      <strong>{signal.signal}</strong>
                      <p>{signal.why_it_matters}</p>
                    </div>
                  ))}
                </div>
              </Panel>
              {missingCriteria.length ? (
                <Panel title={copy.missing}>
                  <ul>
                    {missingCriteria.map((criterion) => (
                      <li key={criterion}>{criterion}</li>
                    ))}
                  </ul>
                </Panel>
              ) : null}
              {observationSummary ? (
                <Panel title={copy.observationTitle}>
                  <div className="grid grid-3">
                    <div>
                      <h3>{copy.observations}</h3>
                      <ul>
                        {observations.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3>{copy.derivations}</h3>
                      <ul>
                        {derivations.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3>{copy.recommendations}</h3>
                      <ul>
                        {recommendations.map((item) => (
                          <li key={item}>{toWorkspaceRecommendation(item, language)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Panel>
              ) : null}
              <Panel title={copy.nextCriteriaTitle} variant="accent">
                <p>{copy.nextCriteriaBody}</p>
                {!processStatus || processStatus === "department_input_open" || processStatus === "hauffe_os_evaluation_ready" ? (
                  <button className="button button-primary" type="button" disabled={isOpeningValueRound} onClick={() => void handleOpenValueRound()}>
                    {isOpeningValueRound ? copy.opening : copy.openValueRound}
                  </button>
                ) : (
                  <a className="button button-primary" href={withLanguagePrefix("/workspace/customer-value", language)}>
                    {nextStepLabel}
                  </a>
                )}
                <button className="button button-secondary" type="button" disabled={isDownloadingPdf} onClick={handleDownloadPdf}>
                  {isDownloadingPdf ? copy.preparingPdf : copy.pdf}
                </button>
                {isOpeningValueRound || isDownloadingPdf ? (
                  <div className="processing-state motion-fade">
                    <HauffeLoader label={isOpeningValueRound ? copy.opening : copy.preparingPdf} />
                    <p className="form-note">{copy.processingData} {copy.processingStay}</p>
                  </div>
                ) : null}
              </Panel>
            </div>
          ) : null}
        </>
      ) : null}
    </Layout>
  );
}
